import { useQuery } from "@tanstack/react-query";
type Recovery = {
  partyBook?: {
    enabled: boolean;
    cutoverAt: string | null;
    eventCount: number;
    openingState: string;
    cashState: string;
  };
  checkedAt: string;
  total: number;
  items: Array<{
    source: string;
    reference: string;
    state: string;
    error_code: string | null;
    created_at: string;
    updated_at: string;
  }>;
  counts: Array<{ source: string; state: string; count: number }>;
  limit: number;
};
const names: Record<string, string> = {
  shop_credit_deficit: "Fee-credit deficit",
  provider_command: "Provider command",
  checkout: "Checkout",
  combined_checkout: "Combined checkout",
  combined_refund: "Combined refund",
  legacy_combined_refund: "Legacy combined refund",
  legacy_cancellation: "Legacy cancellation",
  hub_dash_cancellation: "Hub Dash cancellation",
  hub_dash_tip: "Hub Dash tip",
  hub_dash_refund: "Hub Dash refund",
  shop_billing: "Shop billing",
  promotion_credit: "Promotion credit",
  order_cancellation: "Order cancellation",
  fund_covered_cancellation: "Kindness cancellation",
};
export default function FinanceRecoveryStatus({
  enabled,
}: {
  enabled: boolean;
}) {
  const status = useQuery<Recovery>({
    queryKey: ["admin-finance-recovery"],
    enabled,
    refetchInterval: 30000,
    retry: 1,
    queryFn: async () => {
      const response = await fetch("/api/admin/finance-recovery", {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("ayrshire_token"),
        },
      });
      if (!response.ok) throw Error("recovery_unavailable");
      return response.json();
    },
  });
  return (
    <section
      className="rounded-xl border bg-white p-4 space-y-3"
      aria-label="Payment recovery"
    >
      <div className="flex flex-wrap justify-between gap-2">
        <h2 className="font-semibold">Payment recovery</h2>
        <button
          className="text-sm text-blue-700 underline"
          disabled={status.isFetching}
          onClick={() => void status.refetch()}
        >
          Refresh recovery status
        </button>
      </div>
      <p className="text-sm text-slate-600">
        Waiting work includes unpaid checkouts and pending receipts. A queue
        entry does not prove money was collected or returned. Review entries
        require investigation; this view makes no corrections.
      </p>
      {status.isLoading && <p role="status">Loading recovery status…</p>}
      {status.error && (
        <p role="alert" className="text-sm text-amber-800">
          Recovery status unavailable. Do not assume pending work is complete.
        </p>
      )}
      {status.data?.partyBook && (
        <p className="text-sm text-amber-900">
          Beneficiary journal{" "}
          {status.data.partyBook.enabled ? "active" : "awaiting activation"} ·{" "}
          {status.data.partyBook.eventCount} recorded events. Opening balances
          and bank receipts await review; these entries do not certify available
          cash or bank arrival.
        </p>
      )}
      {status.data && (
        <>
          <p className="text-sm">
            {status.data.total} waiting or under review · checked{" "}
            {new Date(status.data.checkedAt).toLocaleString("en-GB", {
              timeZone: "Europe/London",
            })}
          </p>
          {status.data.total > 0 && (
            <details>
              <summary className="cursor-pointer text-sm font-medium">
                Show recovery references (
                {Math.min(status.data.total, status.data.limit)})
              </summary>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="p-2">Work</th>
                      <th className="p-2">Reference</th>
                      <th className="p-2">State</th>
                      <th className="p-2">Record time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.data.items.map((row) => (
                      <tr
                        className="border-t"
                        key={row.source + ":" + row.reference}
                      >
                        <td className="p-2">
                          {names[row.source] ?? row.source}
                        </td>
                        <td className="p-2 font-mono text-xs break-all">
                          {row.reference}
                        </td>
                        <td className="p-2">
                          {row.state === "review"
                            ? "Review"
                            : row.state === "handed_off"
                              ? "Awaiting receipt"
                              : "Waiting"}
                          {row.error_code && (
                            <span className="block text-xs text-slate-500">
                              {row.error_code}
                            </span>
                          )}
                        </td>
                        <td className="p-2 whitespace-nowrap">
                          {new Date(row.updated_at).toLocaleString("en-GB", {
                            timeZone: "Europe/London",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}
    </section>
  );
}
